import React from 'react';
import { useQuery } from 'react-query';
import { adminStatsAPI } from '../services/adminApi';
import { useAdminSeasonPhaseFilter } from '../hooks/useAdminSeasonPhaseFilter';
import AdminSeasonPhaseFilters from './AdminSeasonPhaseFilters';

const cardStyle = {
  background: 'white',
  borderRadius: '8px',
  padding: '1.25rem 1.5rem',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

const sectionTitle = {
  fontSize: '1.15rem',
  color: '#1f2937',
  margin: '0 0 0.35rem 0',
};

const sectionHint = {
  fontSize: '0.8rem',
  color: '#6b7280',
  margin: '0 0 1rem 0',
  lineHeight: 1.45,
};

const cellStyle = {
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid #e5e7eb',
  fontSize: '0.85rem',
  textAlign: 'left',
};

const MetaCampaignBreakdown = ({ paidRows, freeRows }) => {
  const rowsByKey = new Map();
  const addRows = (rows, field) => {
    (rows || []).forEach((row) => {
      const key = `${row.campaign}||${row.ad}`;
      const existing = rowsByKey.get(key) || {
        campaign: row.campaign,
        ad: row.ad,
        paidTeams: 0,
        freeAccounts: 0,
      };
      existing[field] = Number(row[field === 'paidTeams' ? 'paid_teams' : 'free_accounts']) || 0;
      rowsByKey.set(key, existing);
    });
  };
  addRows(paidRows, 'paidTeams');
  addRows(freeRows, 'freeAccounts');

  const rows = Array.from(rowsByKey.values()).sort(
    (a, b) => b.paidTeams - a.paidTeams || b.freeAccounts - a.freeAccounts
  );

  return (
    <>
      <h3 style={{ ...sectionTitle, fontSize: '1.05rem' }}>Per campaign and per ad</h3>
      <p style={sectionHint}>
        Campaign comes from <code>utm_campaign</code>, the ad row from <code>utm_content</code>. Use
        Meta's dynamic macros in each ad's URL parameters so these fill in automatically.
      </p>
      {rows.length === 0 ? (
        <p style={sectionHint}>No Meta traffic recorded for this phase yet.</p>
      ) : (
        <div style={{ ...cardStyle, padding: 0, overflowX: 'auto', marginBottom: '2rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={cellStyle}>Campaign</th>
                <th style={cellStyle}>Ad</th>
                <th style={{ ...cellStyle, textAlign: 'right' }}>Free accounts</th>
                <th style={{ ...cellStyle, textAlign: 'right' }}>Paid teams</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.campaign}||${row.ad}`}>
                  <td style={cellStyle}>{row.campaign}</td>
                  <td style={cellStyle}>{row.ad}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>{row.freeAccounts}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 600 }}>
                    {row.paidTeams}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

const AdminRegistrationAnalyticsSection = () => {
  const {
    campaignId,
    seasonId: selectedAnalyticsSeason,
    onCampaignChange,
    onPhaseChange,
    seasonOptions,
    phaseOptions,
    allPhases,
    allowAll,
  } = useAdminSeasonPhaseFilter({
    allowAll: true,
    autoDefault: true,
  });

  const {
    data: registrationAnalytics,
    isLoading: analyticsLoading,
    refetch: refetchAnalytics,
  } = useQuery(
    ['registrationAnalytics', campaignId, selectedAnalyticsSeason],
    () => adminStatsAPI.getRegistrationSources(selectedAnalyticsSeason || undefined),
    {
      staleTime: 30000,
    }
  );

  if (analyticsLoading) {
    return <div>Loading registration analytics...</div>;
  }

  if (!registrationAnalytics?.data) {
    return <div>Error loading analytics data</div>;
  }

  const {
    bySource,
    topCampaigns,
    metaStats,
    sourceBreakdown,
    registrationBreakdown,
    funnel = {},
  } = registrationAnalytics.data;

  const paidBySourceTotal = (bySource || []).reduce((sum, item) => sum + parseInt(item.count, 10), 0);
  const metaPaid =
    parseInt(metaStats?.total_meta_registrations, 10) ||
    parseInt(bySource?.find((s) => s.source === 'meta')?.count, 10) ||
    0;
  const metaPercentage =
    paidBySourceTotal > 0 ? ((metaPaid / paidBySourceTotal) * 100).toFixed(1) : '0.0';

  const originalRegistrations = registrationBreakdown?.original_registrations || 0;
  const autoRegistered = registrationBreakdown?.auto_registered || 0;
  const totalTeamsInSeason = registrationBreakdown?.total_teams || paidBySourceTotal;

  const freeAccounts = funnel.freePlayerAccounts ?? 0;
  const abandoned = funnel.abandonedCreatorInvoices ?? 0;
  const pendingInv = funnel.abandonOpenPending ?? funnel.pendingCreatorInvoices ?? 0;
  const leftCancelled = funnel.abandonLeftCancelled ?? 0;
  const rawCancelledInv = funnel.cancelledCreatorInvoices ?? 0;
  const retriesThenPaid = funnel.checkoutRetriesThenPaid ?? 0;
  const paidInvoices = funnel.paidCreatorInvoices ?? 0;
  const accountToPaidRate =
    freeAccounts > 0 ? ((paidInvoices / freeAccounts) * 100).toFixed(1) : null;

  const getSourceIcon = (source) => {
    switch (source) {
      case 'meta':
        return '📱';
      case 'google':
        return '🔍';
      case 'direct':
        return '🔗';
      case 'referral':
        return '👥';
      case 'email':
        return '📧';
      case 'organic':
        return '🌱';
      case 'admin':
        return '🛠️';
      default:
        return '❓';
    }
  };

  const selectedSeasonName = selectedAnalyticsSeason
    ? allPhases.find((s) => String(s.id) === String(selectedAnalyticsSeason))?.name ||
      'Selected Phase'
    : 'All Phases';

  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        <div>
          <h2 style={{ fontSize: '2rem', color: '#1f2937', margin: 0 }}>Registration Analytics</h2>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginTop: '0.5rem',
              fontSize: '0.875rem',
              color: '#6b7280',
            }}
          >
            <span>Phase:</span>
            <span
              style={{
                fontWeight: 600,
                color: selectedAnalyticsSeason ? '#043663' : '#6b7280',
                padding: '0.25rem 0.75rem',
                background: selectedAnalyticsSeason ? '#dbeafe' : '#f3f4f6',
                borderRadius: '4px',
              }}
            >
              {selectedSeasonName}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <AdminSeasonPhaseFilters
            campaignId={campaignId}
            seasonId={selectedAnalyticsSeason}
            seasonOptions={seasonOptions}
            phaseOptions={phaseOptions}
            onCampaignChange={onCampaignChange}
            onPhaseChange={onPhaseChange}
            allowAll={allowAll}
          />
          <button
            type="button"
            onClick={() => refetchAnalytics()}
            style={{
              padding: '0.5rem 1rem',
              background: '#043663',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      <div
        style={{
          ...cardStyle,
          marginBottom: '1.5rem',
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
        }}
      >
        <p style={{ margin: 0, fontSize: '0.875rem', color: '#334155', lineHeight: 1.55 }}>
          <strong>How to read this page:</strong> The big “all free accounts” number is{' '}
          <em>every</em> player who signed up in this phase’s registration window — not Meta ads.
          Use <strong>Meta free accounts</strong> / <strong>Meta paid teams</strong> for ad
          attribution (needs <code>fbclid</code> or Meta UTM on the landing link). Named campaign
          counts need <code>utm_campaign</code> on your ad URL. Meta Events Manager shows{' '}
          <code>Lead</code> / <code>CompleteRegistration</code> only after this tracking code is
          live on production and the browser is not blocking the Pixel.
        </p>
      </div>

      {/* Funnel */}
      <h3 style={sectionTitle}>Signup → pay funnel</h3>
      <p style={sectionHint}>
        Left side = overall league activity for this phase filter. Right side = Meta-attributed only
        (will stay near zero until ads run with proper URLs, or until new signups after attribution
        storage is deployed).
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem',
          marginBottom: '1rem',
        }}
      >
        <div style={cardStyle}>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.35rem' }}>
            All free player accounts
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#043663' }}>{freeAccounts}</div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.35rem' }}>
            Every signup in this phase window (not Meta-only)
          </div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.35rem' }}>
            Truly abandoned checkouts
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#d97706' }}>{abandoned}</div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.35rem', lineHeight: 1.45 }}>
            Unique player+phase that never paid
            <br />
            Still open: {pendingInv} · Left cancelled: {leftCancelled}
            <br />
            <span style={{ color: '#94a3b8' }}>
              Raw cancelled invoices: {rawCancelledInv} (includes Pay-again retries;{' '}
              {retriesThenPaid} later paid)
            </span>
          </div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.35rem' }}>
            Paid team registrations
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#16a34a' }}>{paidInvoices}</div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.35rem' }}>
            Creator invoices paid
            {accountToPaidRate != null ? ` · ${accountToPaidRate}% of free accounts` : ''}
          </div>
        </div>
      </div>

      <h3 style={{ ...sectionTitle, fontSize: '1.05rem' }}>Meta ads attribution (this phase)</h3>
      <p style={sectionHint}>
        These stay at 0 until people land with your ad URL (
        <code>?utm_source=facebook&amp;utm_campaign=YOUR_CAMPAIGN&amp;fbclid=…</code>) and complete
        signup / pay. Old Facebook shares with an <code>fbclid</code> but no campaign name show as
        “fbclid only”.
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem',
        }}
      >
        <div style={{ ...cardStyle, borderTop: '4px solid #1877f2' }}>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.35rem' }}>
            Meta free accounts (Lead)
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1877f2' }}>
            {funnel.freeMetaAccounts ?? 0}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.35rem' }}>
            With named campaign: {funnel.freeMetaWithCampaign ?? 0}
          </div>
        </div>
        <div style={{ ...cardStyle, borderTop: '4px solid #1877f2' }}>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.35rem' }}>
            Meta paid teams
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1877f2' }}>{metaPaid}</div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.35rem' }}>
            Named campaign: {funnel.paidMetaWithCampaign ?? 0} · fbclid only:{' '}
            {funnel.paidMetaFbclidOnly ?? 0}
          </div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.35rem' }}>
            Meta share of paid teams
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1f2937' }}>{metaPercentage}%</div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.35rem' }}>
            Of paid teams by source ({metaPaid}/{paidBySourceTotal || 0})
          </div>
        </div>
      </div>

      <MetaCampaignBreakdown
        paidRows={funnel.metaPaidTeamsByCampaign}
        freeRows={funnel.metaFreeAccountsByCampaign}
      />

      {/* Phase roster context */}
      <h3 style={sectionTitle}>Phase roster (all seats)</h3>
      <p style={sectionHint}>
        Total seats in the phase (includes auto-registered / carried). Source breakdown below uses{' '}
        <strong>paid confirmed</strong> signup seats only.
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem',
        }}
      >
        <div style={cardStyle}>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.35rem' }}>
            Total teams in phase
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1f2937' }}>
            {totalTeamsInSeason}
          </div>
          {selectedAnalyticsSeason && registrationBreakdown && (
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', lineHeight: 1.5 }}>
              <div>Original: {originalRegistrations}</div>
              <div>Auto-registered: {autoRegistered}</div>
            </div>
          )}
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.35rem' }}>
            Meta campaigns (paid)
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1f2937' }}>
            {metaStats?.unique_campaigns || 0}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.35rem' }}>
            Distinct utm_campaign on paid Meta teams
          </div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.35rem' }}>
            Meta clicks (paid)
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1f2937' }}>
            {metaStats?.unique_clicks || 0}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.35rem' }}>
            Distinct fbclid on paid Meta teams
          </div>
        </div>
      </div>

      {/* Paid by source */}
      <div style={{ ...cardStyle, marginBottom: '1.5rem' }}>
        <h3 style={{ ...sectionTitle, borderBottom: '2px solid #e5e7eb', paddingBottom: '0.5rem' }}>
          Paid registrations by source
        </h3>
        <p style={sectionHint}>Only paid confirmed team seats (not abandoned checkouts).</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {(bySource || []).length === 0 ? (
            <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>No paid registrations in this filter.</div>
          ) : (
            bySource.map((source) => (
              <div
                key={source.source}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem',
                  background: '#f9fafb',
                  borderRadius: '6px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 500 }}>
                  <span>{getSourceIcon(source.source)}</span>
                  <span style={{ textTransform: 'capitalize' }}>{source.source}</span>
                </div>
                <div>
                  <span style={{ fontWeight: 600, color: '#043663' }}>{source.count}</span>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280', marginLeft: '0.5rem' }}>
                    ({parseFloat(source.percentage || 0).toFixed(1)}%)
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {topCampaigns && topCampaigns.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: '1.5rem' }}>
          <h3 style={{ ...sectionTitle, borderBottom: '2px solid #e5e7eb', paddingBottom: '0.5rem' }}>
            Top campaigns (paid teams)
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {topCampaigns.map((campaign, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.5rem',
                  fontSize: '0.875rem',
                }}
              >
                <div>
                  <span style={{ fontWeight: 500 }}>{campaign.utm_campaign}</span>
                  <span style={{ marginLeft: '0.5rem', color: '#6b7280', fontSize: '0.75rem' }}>
                    ({campaign.registration_source})
                  </span>
                </div>
                <div style={{ fontWeight: 600, color: '#043663' }}>{campaign.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {sourceBreakdown && sourceBreakdown.length > 0 && (
        <div style={cardStyle}>
          <h3 style={{ ...sectionTitle, borderBottom: '2px solid #e5e7eb', paddingBottom: '0.5rem' }}>
            Seat payment by source
          </h3>
          <p style={sectionHint}>
            Teams linked to this phase: how many have a paid season registration (tsr.payment_status).
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {sourceBreakdown.map((source) => (
              <div
                key={source.source}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem',
                  background: '#f9fafb',
                  borderRadius: '6px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 500 }}>
                  <span>{getSourceIcon(source.source)}</span>
                  <span style={{ textTransform: 'capitalize' }}>{source.source}</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 600, color: '#043663' }}>
                    {source.paid_registrations}/{source.total_registrations} paid
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                    {parseFloat(source.conversion_rate || 0).toFixed(1)}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRegistrationAnalyticsSection;
